import type { DateTime } from "luxon";
import { getWeekStart } from "./dateUtils";
import { withDetails } from "./departmentWeeks";
import {
	type BaseNurseShift,
	type Day,
	DEFAULT_WEEK_START_DAY,
	type ScheduleActualShift,
	type ScheduleDay,
	type SchedulePersonField,
	type ScheduleShift,
	type Shift,
	type ShiftPerson,
	type WeekData,
} from "./models";
import { toIsoDate } from "./scheduleWeeks";

const DAYS_PER_WEEK = 7;
/** A panel reads the date's week and this many weeks on each side: enough for its neighbour-day checks. */
const WINDOW_WEEKS_AROUND = 1;

// DEFAULT: the cache has no week summary; nothing a panel runs reads it.
const NO_WEEK_SUMMARY: Omit<WeekData, "weeklyShifts"> = {
	weekScore: "",
	weekLoad: "",
	weekSeason: "",
	workForce: "",
	loadText: "",
	seasonText: "",
	workForceText: "",
	weekDifficulty: "",
};

/** The first and last day a panel reads around `date`: whole Sunday weeks, as the old list's weeks are. */
export const panelWindowOf = (date: DateTime): { from: DateTime; to: DateTime } => {
	const weekStart = getWeekStart(date, DEFAULT_WEEK_START_DAY);
	return {
		from: weekStart.minus({ weeks: WINDOW_WEEKS_AROUND }),
		to: weekStart.plus({ weeks: WINDOW_WEEKS_AROUND, days: DAYS_PER_WEEK - 1 }),
	};
};

/** The shift types a week's days list, in their order (the server's: Day before Night). */
const shiftTypesOf = (days: ScheduleDay[]): string[] => [...new Set(days.flatMap((day) => Object.keys(day.byShiftType)))];

/** The `shiftType` shifts of `shifts` with their nurse's display fields; like the provider, a shift whose nurse isn't on the roster is dropped. */
const rowShifts = <Shift extends ScheduleShift | ScheduleActualShift>(
	shifts: Shift[],
	shiftType: string,
	getPerson: (id: number) => ShiftPerson | undefined,
	lastNameFirst: boolean,
): (Shift & Pick<BaseNurseShift, SchedulePersonField>)[] =>
	shifts.flatMap((shift) => {
		const nurse = getPerson(shift.nurseId);
		return shift.shiftType === shiftType && nurse ? [withDetails(shift, nurse, lastNameFirst)] : [];
	});

const rowDayOf = (day: ScheduleDay, shiftType: string, getPerson: (id: number) => ShiftPerson | undefined, lastNameFirst: boolean): Day => ({
	// The server copies department_data's numbers for the shift type, nulls included; Day's types predate the nulls.
	...(day.byShiftType[shiftType] as Omit<Day, "date" | "nurses" | "nursesActual" | "census_override" | "census_grouping">),
	date: day.date,
	...(day.census_override && { census_override: day.census_override }),
	...(day.census_grouping && { census_grouping: day.census_grouping }),
	nurses: rowShifts(day.scheduled, shiftType, getPerson, lastNameFirst),
	nursesActual: rowShifts(day.actual, shiftType, getPerson, lastNameFirst),
});

/**
 * Schedule cache days as the old list's weeks: one week per Sunday week, a row per shift type, and each row only
 * on the days that shift type has staffing for, as `department_data` builds them. The server groups shifts into rows
 * by their own shift type, so filtering a day's one list by shift type gives the same rows back.
 */
export const panelWeeksOf = (days: ScheduleDay[], getPerson: (id: number) => ShiftPerson | undefined, lastNameFirst: boolean): WeekData[] => {
	const daysByWeek = new Map<string, ScheduleDay[]>();
	for (const day of days) {
		const weekStart = toIsoDate(getWeekStart(day.date, DEFAULT_WEEK_START_DAY));
		const weekDays = daysByWeek.get(weekStart);
		if (weekDays) weekDays.push(day);
		else daysByWeek.set(weekStart, [day]);
	}
	return [...daysByWeek.values()].map((weekDays) => ({
		...NO_WEEK_SUMMARY,
		weeklyShifts: shiftTypesOf(weekDays).map((shiftType) => ({
			type: shiftType,
			days: weekDays.filter((day) => shiftType in day.byShiftType).map((day) => rowDayOf(day, shiftType, getPerson, lastNameFirst)),
		})),
	}));
};

/**
 * `day` after `edit`, an edit written for the old list's weeks: it runs on the day built as those weeks (a row per shift
 * type), and the rows' shifts become the day's lists again. The census isn't read back: census edits patch the cached
 * day themselves. A shift no row holds (its person isn't on the roster, or its type has no row) isn't shown, so the
 * edit can't reach it and it stays as it was.
 */
export const editCachedDay = (
	day: ScheduleDay,
	edit: (weeks: WeekData[]) => WeekData[],
	getPerson: (id: number) => ShiftPerson | undefined,
	lastNameFirst: boolean,
): ScheduleDay => {
	const rowDays = edit(panelWeeksOf([day], getPerson, lastNameFirst)).flatMap((week) => week.weeklyShifts.flatMap((row) => row.days));
	const isInNoRow = (shift: ScheduleShift | ScheduleActualShift) => getPerson(shift.nurseId) === undefined || !(shift.shiftType in day.byShiftType);
	return {
		...day,
		scheduled: [...rowDays.flatMap((rowDay) => rowDay.nurses), ...day.scheduled.filter(isInNoRow)],
		actual: [...rowDays.flatMap((rowDay) => rowDay.nursesActual ?? []), ...day.actual.filter(isInNoRow)],
	};
};

/** The index of the week holding `date` in `weeks`, by its first row's days, or -1: the provider's `getWeekIndexByDate`. */
export const weekIndexOfDate = (weeks: WeekData[], date: DateTime): number => {
	const day = date.startOf("day");
	return weeks.findIndex((week) => {
		const rowDays = week.weeklyShifts[0]?.days;
		return rowDays !== undefined && rowDays.length > 0 && rowDays[0].date <= day && rowDays[rowDays.length - 1].date >= day;
	});
};

/** The index of `date` in its week's first row, or -1: the provider's `getDayIndexByDate`. */
export const dayIndexOfDate = (weeks: WeekData[], date: DateTime): number => {
	const weekIndex = weekIndexOfDate(weeks, date);
	if (weekIndex === -1) return -1;
	const isoDate = toIsoDate(date);
	return weeks[weekIndex].weeklyShifts[0].days.findIndex((day) => toIsoDate(day.date) === isoDate);
};

/**
 * The first loaded day of the old list: the earliest date a panel's picker reaches with the cache off. Skips a week
 * whose days aren't hydrated yet (the provider's own lookups guard the same way). `undefined` when nothing is loaded.
 */
export const getFirstLoadedDate = (weeks: readonly WeekData[]): DateTime | undefined =>
	weeks.map((week) => week.weeklyShifts[0]?.days[0]?.date).find((date): date is DateTime => date !== undefined);

/**
 * The first shift type `date` has in `weeks`, for a panel opened on a whole day: the panel re-derives the real shift
 * from the times picked, so it only has to be one the day has. Undefined when the day isn't in `weeks`.
 */
export const firstShiftTypeOn = (weeks: readonly WeekData[], date: DateTime): string | undefined => {
	const isoDate = toIsoDate(date);
	return weeks.flatMap((week) => week.weeklyShifts).find((row) => row.days.some((day) => toIsoDate(day.date) === isoDate))?.type;
};

/** The week's dates in order, from every row: a row lists only the days its shift type has staffing for. */
export const datesOfWeek = (week: WeekData): DateTime[] => {
	const datesByIso = new Map<string, DateTime>();
	for (const row of week.weeklyShifts) {
		for (const day of row.days) {
			const isoDate = toIsoDate(day.date);
			if (!datesByIso.has(isoDate)) datesByIso.set(isoDate, day.date);
		}
	}
	return [...datesByIso.entries()].sort(([isoDate], [otherIsoDate]) => isoDate.localeCompare(otherIsoDate)).map(([, date]) => date);
};

/** The one day index in rows cut by `rowsOnDate`. */
export const ONLY_DAY_INDEX = 0;

/** Each row that has `date`, holding only that day, so `ONLY_DAY_INDEX` is `date` in every row whatever days a row skips. */
export const rowsOnDate = (rows: readonly Shift[], date: DateTime): Shift[] => {
	const isoDate = toIsoDate(date);
	return rows.flatMap((row) => {
		const day = row.days.find((rowDay) => toIsoDate(rowDay.date) === isoDate);
		return day ? [{ ...row, days: [day] }] : [];
	});
};
